import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { baseStyles } from '../styles/shared.js';
import type { SysMLElement } from '@arctek/sysml-types';

// ---------------------------------------------------------------------------
// <sysml-breadcrumbs> — Cursor scope path display
//
// Shows the element path at the cursor: File > Package > PartDef > attribute
// Each segment is clickable and dispatches @breadcrumb-navigate with the
// element id so the workspace can reveal that scope in the editor.
// ---------------------------------------------------------------------------

@customElement('sysml-breadcrumbs')
export class SysmlBreadcrumbs extends LitElement {
  static override styles = [
    baseStyles,
    css`
      :host {
        display: flex;
        align-items: center;
        gap: 2px;
        min-width: 0;
        overflow: hidden;
        font-size: var(--r5-font-xs);
        color: var(--r5-text-muted);
        padding: 0 var(--r5-space-2);
        height: 22px;
        flex-shrink: 1;
      }

      .crumb {
        display: flex;
        align-items: center;
        gap: 2px;
        cursor: pointer;
        white-space: nowrap;
        padding: 1px 4px;
        border-radius: var(--r5-radius-sm);
        transition: background var(--r5-duration-fast) var(--r5-ease-default);
      }
      .crumb:hover {
        background: color-mix(in srgb, var(--r5-text) 8%, transparent);
        color: var(--r5-text);
      }

      .separator {
        color: var(--r5-text-muted);
        opacity: 0.5;
        flex-shrink: 0;
      }

      nav {
        display: contents;
      }

      .type-label {
        opacity: 0.6;
        font-size: 0.85em;
      }
    `,
  ];

  /** The document name shown as the first breadcrumb */
  @property() documentName = '';

  /** The element path from root to cursor (broadest first, most specific last) */
  @property({ type: Array }) elementPath: SysMLElement[] = [];

  override render() {
    const parts: unknown[] = [];

    // Document name as first crumb (non-clickable, just context)
    if (this.documentName) {
      parts.push(html`<span class="crumb" @click=${this._onDocClick}>${this.documentName}</span>`);
    }

    // Element path
    for (let i = 0; i < this.elementPath.length; i++) {
      const el = this.elementPath[i];
      if (parts.length > 0) {
        parts.push(html`<span class="separator">/</span>`);
      }
      parts.push(html`
        <span class="crumb" @click=${() => this._onElementClick(el)}>
          <span class="type-label">${this._shortType(el.elementType)}</span>
          ${el.name}
        </span>
      `);
    }

    return parts.length > 0
      ? html`<nav role="navigation" aria-label="Breadcrumb">${parts}</nav>`
      : nothing;
  }

  /** Abbreviate element types for compact display */
  private _shortType(type: string): string {
    const map: Record<string, string> = {
      package: 'pkg',
      partDefinition: 'part def',
      partUsage: 'part',
      actionDefinition: 'action def',
      actionUsage: 'action',
      requirementDefinition: 'req def',
      requirementUsage: 'req',
      constraintDefinition: 'constraint',
      portDefinition: 'port def',
      stateDefinition: 'state def',
      verificationDefinition: 'verify',
      connectionDefinition: 'conn',
      attributeUsage: 'attr',
    };
    return map[type] ?? '';
  }

  private _onDocClick(): void {
    this.dispatchEvent(
      new CustomEvent('breadcrumb-navigate', {
        detail: { type: 'document' },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onElementClick(el: SysMLElement): void {
    this.dispatchEvent(
      new CustomEvent('breadcrumb-navigate', {
        detail: { type: 'element', elementId: el.id, source: el.source },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-breadcrumbs': SysmlBreadcrumbs;
  }
}
